import { COLORS, ContentIcons, LoadingIndicator, PageWidth } from "@laerdal/life-react-components";
import axios from "axios";
import Topics from "../../commonComponents/Topics";
import Faqs from "../../commonComponents/Faqs";
import CantFindCreateCase from "../../commonComponents/CantFindCreateCase";
import CantFindWhatYouNeed from "../../commonComponents/CantFindWhatYouNeed";
import BigSmallContent from "../../commonComponents/BigSmallContent";
import styled from "styled-components";
import ProductTopSection from "../../commonComponents/ProductTopSection";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import ProductApi from "../../services/api/ProductApi";
import { ProductCategoryDetailsDto } from "../../model/dto/productCategoryDetailsDto";
import { useTranslation } from "react-i18next";
import { marketList } from "../../model/constants/marketContants";
import TranslateWithAI from "../../commonComponents/TranslateWithAI";
import posthog from 'posthog-js';
import { useSelector } from "react-redux";
import { selectMarket, selectTranslateWithAi } from "../../store/slices/globalConfigSlice";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap:32px;
`;

const ProductCategoryPage = () => {
    const { categoryLink } = useParams<{ categoryLink: string }>();
    const { t } = useTranslation("landingPage");

    const [categoryLoading, setCategoryLoading] = useState<boolean>(true);
    const [category, setCategory] = useState<ProductCategoryDetailsDto | undefined>(undefined);

    const currentMartket = useSelector(selectMarket);
    const translateWithAi = useSelector(selectTranslateWithAi);

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;
        if (categoryLink) {
            ProductApi
                .GetProductCategory(categoryLink, (categoryLink != 'medical-devices' && translateWithAi) ? marketList[currentMartket].locale : undefined, signal)
                .then((data) => {
                    setCategoryLoading(false);
                    setCategory(data);
                })
                .catch((error) => {
                    if (axios.isCancel(error)) return;
                })
        }
        return () => {
            controller.abort();
        };
    }, [categoryLink, translateWithAi, currentMartket]);


    return (
        <>
            {categoryLoading &&
                <LoadingIndicator></LoadingIndicator>
            }

            {category &&
                <>
                    <ProductTopSection
                        title={t(category.name)}
                        description={t(category.description)}
                        asset={category.mainImageUrl}
                        breadcrumb={[{
                            text: t('Home'),
                            link: `/${currentMartket}`
                        }, {
                            text: t(category.name)
                        }]}
                    />

                    <PageWidth $useMaxWidth={true} $maxWidth={1024} style={{ width: "calc(100% - 32px)" }}>
                        <PageContainer>
                            <Topics
                                items={
                                    category.products.map((x) => ({
                                        text: x.name,
                                        asset: x.mainImageUrl,
                                        heightPx: "144px",
                                        link: x.redirectUrl ? x.redirectUrl : `product/${x.link}`
                                    }))
                                }
                                onItemClick={(item) => {
                                    const product = category.products.find(p => p.name === item.text);
                                    posthog?.capture('SUPP-CategoryPage-ProductLineClick', {
                                        categorycode: categoryLink,
                                        categorytext: category.name,
                                        productlinecode: product?.link,
                                        productlinetext: item.text,
                                    });
                                }}
                            />

                            {marketList[currentMartket].locale != 'en' && <TranslateWithAI isMedicalProduct={categoryLink == 'medical-devices'} />}
                            <Faqs
                                title={t('FAQs')}
                                items={
                                    category.faqs.map((x) => ({
                                        link: `/articles/Knowledge/${x.link}`,
                                        text: x.name
                                    }))}
                            />
                            <CantFindWhatYouNeed />
                            <BigSmallContent
                                big={{
                                    title: t('Laerdal Global Warranty'),
                                    description: t('globalWarrantyDescription'),
                                    linkText: t('pdf'),
                                    linkUrl: "https://cdn.laerdal.com/downloads/f4781/Att1toPRO-ML01-0295.pdf",
                                }}
                                small={{
                                    title: t('Privacy Statement'),
                                    description: t('privacyStatementDescription'),
                                    linkText: t('readMore'),
                                    linkUrl: "https://www.laerdal.com/support/customer-service/privacy-policy/"
                                }} />
                            <CantFindCreateCase />
                        </PageContainer>
                    </PageWidth>
                </>
            }
        </>);
};

export default ProductCategoryPage;